<template>
  <div class="modal-card">
    <b-loading :isFullPage="false" :active="isLoading" />
    <header class="modal-card-head">
      <h1 class="modal-card-title is-size-5 has-text-centered">
        {{ General.INFO_MANDATORY_SIGNATURE }}
      </h1>
      <div class="close-button" @click.stop="close()">
        <b-icon icon="close" />
      </div>
    </header>
    <h3 class="is-size-custom has-text-centered mx-15 px-20">
      {{ App.ModalDocMandatorySignature.SUBTITLE }}
    </h3>
    <div class="modal-content-container">
      <section class="spacing-p-5">
        <b-field
          v-if="!disableFields"
          customClass="has-text-color-7"
          :label="General.ORGAN_ENTITY"
        >
          <b-autocomplete
            v-model="entityName"
            :placeholder="General.PLACEHOLDER.SEARCH_ORGAN_ENTITY"
            keepFirst
            :openOnFocus="openOnFocus"
            :data="filteredParts(entityName)"
            field="name"
            @select="option => (selectedEntity = option)"
          />
        </b-field>

        <b-field v-if="!disableFields">
          <template slot="label">
            <span class="has-text-color-7">
              {{ General.SUBSCRIBER }}
            </span>
          </template>
        </b-field>
        <b-field v-if="!disableFields" grouped expanded>
          <b-autocomplete
            :value="subscriberName"
            :disabled="!selectedEntity"
            :placeholder="General.PLACEHOLDER.SEARCH_USERNAME"
            :loading="isFetching"
            keepFirst
            :openOnFocus="openOnFocus"
            :data="getUsersFromSearchFormat"
            field="displayName"
            expanded
            class="mb-15"
            data-cy="mandatory-subscriber-name"
            @typing="filteredSubscriberData"
            @select="selectSubscriber"
          />
        </b-field>

        <draggable v-if="subscribersList.length > 0" v-model="subscribersList">
          <CustomSubscriberList
            v-for="(item, index) in subscribersList"
            :key="index"
            :subscriber="item"
            :class="{ disabled: disableFields }"
          />
        </draggable>
        <div class="spacing-py-5"></div>
      </section>
    </div>
    <footer
      v-if="!disableFields"
      class="modal-card-foot is-justify-content-flex-end"
    >
      <b-button
        :type="isContrast ? 'is-white-dark' : 'is-primary'"
        :disabled="!subscribersList.length"
        @click="saveMandatorySignature"
      >
        {{ General.SAVE.toUpperCase() }}
      </b-button>
      <b-button
        :type="isContrast ? 'is-white-dark' : 'is-primary'"
        outlined
        @click="close"
      >
        {{ General.CANCEL.toUpperCase() }}
      </b-button>
    </footer>
  </div>
</template>

<script>
import { mapGetters, mapActions, mapMutations } from "vuex";
import debounce from "lodash/debounce";
import draggable from "vuedraggable";

import CustomSubscriberList from "@/components/CustomSubscriberList";
import { App, General } from "@/utils/constants";

export default {
  name: "ModalDocMandatorySignature",

  components: {
    CustomSubscriberList,
    draggable
  },

  props: {
    filled: {
      type: Array,
      default: () => []
    },

    updateFiles: {
      type: Function,
      default: null
    },

    processId: {
      type: String,
      default: null
    },

    joinId: {
      type: String,
      default: null
    }
  },

  data() {
    return {
      App,
      General,
      isLoading: false,
      openOnFocus: false,
      entityName: "",
      subscriberName: "",
      selectedEntity: null,
      selectedSubscriber: null,
      subscribers: [],
      isSending: false,
      isFetching: false
    };
  },

  computed: {
    ...mapGetters({
      user: "getCurrentUser",
      getUsersFromSearch: "getUsersFromSearch",
      getSubscribers: "getSubscribers",
      getSubscribersError: "getSubscribersError",
      getCurrentProcessFileId: "getCurrentProcessFileId",
      filteredParts: "getFilteredParts",
      getPartsById: "getPartsById",
      isContrast: "isContrast"
    }),
    subscribersList: {
      get() {
        return this.getSubscribers;
      },
      set(value) {
        const newList = value.map((subscriber, index) => ({
          ...subscriber,
          order: index
        }));
        this.$store.commit("updateSubscriderOrder", newList);
      }
    },

    disableFields() {
      return Boolean(this.filled.length);
    },

    getUsersFromSearchFormat() {
      return this.getUsersFromSearch.map(user => ({
        ...user,
        displayName: `${user.name}${
          user.capacity_selected
            ? " - " + user.capacity_selected.abbreviation
            : ""
        }`
      }));
    }
  },

  beforeMount() {
    this.clearError();
    this.$store.commit("resetSubscriberStore");
    this.selectedEntity = this.getPartsById(this.user.capacity.part_id);
    this.entityName = this.selectedEntity.name;

    if (this.filled.length > 0) {
      this.subscribersList = this.filled.map(request => ({
        name: request.user_name,
        ...request,
        id: "",
        bonds: [
          {
            part_abbreviation: request.capacity.entity.abbreviation,
            capacity_abbreviation: request.capacity.abbreviation,
            capacity_description: request.capacity.description
          }
        ]
      }));
    }
  },

  methods: {
    ...mapActions({
      userFilter: "userGenericFilterRequest",
      createObrigatorySignature: "createObrigatorySignature"
    }),

    ...mapMutations({
      clearError: "clearError"
    }),

    selectSubscriber(selectedSubscriber) {
      this.clearError();
      if (selectedSubscriber) {
        this.$store.commit("addSubscriber", selectedSubscriber);
        this.subscriberName = "";
      }
    },

    close() {
      this.$parent.close();
      this.$store.commit("resetSubscriberStore");
      this.updateFiles && this.updateFiles();
    },

    async saveMandatorySignature() {
      this.isLoading = true;
      const listUsers = this.subscribersList.map(sub => ({
        user_id: sub.id,
        process_file: this.getCurrentProcessFileId,
        user_name: sub.name,
        user_capacity: sub.capacity_selected.id
      }));
      const result = await this.createObrigatorySignature(listUsers);
      if (result) {
        await this.$store.commit("setCurrentProcessFileId", null);
        this.close();
      }
      this.isLoading = false;
    },

    filteredSubscriberData: debounce(async function(name) {
      this.subscriberName = name;

      if (name === "") {
        this.$store.commit("setUsersFromSearch", []);
        return;
      }
      this.isFetching = true;
      await this.userFilter({
        part_id: this.selectedEntity.id,
        name
      });
    }, 500)
  }
};
</script>

<style lang="scss" scoped>
@import "@/assets/scss/variables";
.modal-card {
  min-width: 600px;
  max-height: 75vh;
  height: 100%;
  background-color: $color-1;
  color: $color-7;

  @media screen and (max-width: $tablet) {
    min-width: auto;
  }
}

.modal-card-body {
  padding: 0 15px 15px 15px;
}

.modal-content-container {
  display: flex;
  flex-grow: 1;
  flex-direction: column;
  justify-content: space-between;
  overflow: auto;
  padding: 10px 15px 0;
}

.disabled {
  background-color: $background-color;
}

.is-size-custom {
  font-size: 0.95rem;
}
</style>
